'use client'

import { useState, useEffect, useMemo, useRef } from 'react'
import { useRouter, useParams } from 'next/navigation'
import Link from 'next/link'
import { TERMS_DATE } from '@/components/legal-footer'
import { useSession } from 'next-auth/react'
import { ChevronDown, ChevronRight, Menu, X, ChevronLeft, NotebookPen } from 'lucide-react'
import Image from 'next/image'
import { Button } from '@/components/ui/button'
import { SupporterBadge } from '@/components/ui/supporter-badge'
import { PioneerBadge } from '@/components/ui/pioneer-badge'
import { PIONEER_PLAN_SLUG } from '@/lib/billing'
import { ReadingProgress } from './reading-progress'
import { useThemePreviewOverride } from './theme-preview'
import type { SiteTheme } from '@/lib/site-theme'
import { PublicThemeToggle } from './theme-toggle'
import { PublicSoundToggle } from './sound-toggle'
import { ReflowToggle } from './reflow-toggle'
import { PrintButton, PrintView } from '@/components/print/print-view'
import { AuthButton } from './auth-button'
import { FontSizeControls } from './font-size-controls'
import { SyncStatusButton } from '@/components/ui/sync-status'
import { InlineMarkdown } from '@/components/ui/inline-markdown'
import { useLayout } from '@/contexts/layout-context'
import { useCurrentSite } from '@/contexts/current-site-context'
import { TeacherClassProvider } from '@/contexts/teacher-class-context'
import { AdminToolbox } from './admin-toolbox'
import { LockdownWatcher } from './lockdown-watcher'

interface Teacher {
  name: string | null
  pageSlug: string
  pageName?: string | null
  pageDescription?: string | null
  pageIcon?: string | null
  titleStyle?: string | null
  logoUrl?: string | null
  supporterBadgeHidden?: boolean | null
  supporterBadgeMessage?: string | null
  bio?: string | null
  title?: string | null
  // Used by AuthButton to suppress the login affordance on free teacher pages
  // (see src/components/public/auth-button.tsx).
  billingPlan?: string | null
}

interface SiteStructure {
  id: string
  title: string
  accentColor?: string | null // Hex color for letter markers
  skripts: {
    id: string
    title: string
    slug: string
    order?: number // Position within collection (0-indexed) for letter markers
    hasFrontpage?: boolean // Whether the skript has a frontpage
    pages: {
      id: string
      title: string
      slug: string
      pageType?: string | null
    }[]
  }[]
}

interface RootSkript {
  id: string
  title: string
  description: string | null
  slug: string
  hasFrontpage?: boolean
  collection: { title: string }
  pages: Array<{ id: string, title: string, slug: string, pageType?: string | null }>
}

export type SidebarItem =
  | { kind: 'collection'; data: SiteStructure }
  | { kind: 'skript'; data: RootSkript }

interface PublicSiteLayoutProps {
  teacher: Teacher
  siteStructure: SiteStructure[]
  rootSkripts?: RootSkript[]
  // Interleaved collections + root skripts in site-builder order. When omitted,
  // the layout falls back to collections-first then root skripts (preserves
  // the legacy two-section behavior for callers that haven't migrated).
  sidebarItems?: SidebarItem[]
  children: React.ReactNode
  currentPath?: string
  fullSiteStructure?: SiteStructure[] // Full site structure when sidebarBehavior is "full"
  sidebarBehavior?: 'contextual' | 'full'
  // Preset (data-typography), reading-progress flag and sanitized custom CSS.
  // Built server-side by readSiteTheme() (src/lib/site-theme.ts).
  theme?: SiteTheme
  pageId?: string // Page ID for lazy edit permission check
  routePrefix?: string // Server-side route prefix (e.g., '/org/eduskript/c' for org content, '/{pageSlug}' for teacher pages). Used to build navigation URLs; the proxy-stripped portion is removed at click time so the link matches the browser-visible path.
  homeUrl?: string // Explicit home/logo target. Defaults to routePrefix. Required when routePrefix is a content prefix that isn't itself a valid route (e.g. '/org/{slug}/c' — pass homeUrl='/org/{slug}' to point at the org root instead).
  hideSidebar?: boolean // Hide sidebar entirely (e.g., exam pages)
}

// Hosts where the proxy rewrites `/foo` → `/org/${PRIMARY_ORG}/foo` by default.
// Everything else is treated as a custom host (teacher domain like
// informatikgarten.ch, DB-resolved org domain like schoola.org) where the
// proxy always prepends the tenant segment based on hostname resolution.
// PRIMARY_ORG must stay in sync with DEFAULT_ORG_SLUG in src/proxy.ts.
const PRIMARY_ORG = 'eduskript'
const PRIMARY_HOSTS = ['eduskript.org', 'www.eduskript.org', 'localhost']

function isPrimaryHost(hostname: string): boolean {
  return PRIMARY_HOSTS.includes(hostname)
    || hostname.endsWith('.ngrok-free.dev')
    || hostname.endsWith('.ngrok-free.app')
    || hostname.endsWith('.ngrok.io')
}

// Determine what portion of `routePrefix` the proxy prepended for the current
// host. Returns '' when the browser URL is already the full internal path
// (dev direct access, or the `/org/otherOrg/...` fallback on the primary host
// which the proxy bypasses).
export function computeProxyStrip(hostname: string, routePrefix: string): string {
  if (isPrimaryHost(hostname)) {
    // Primary host: proxy only rewrites to `/org/${PRIMARY_ORG}`. Routes under
    // a different org (the no-custom-domain fallback) aren't rewritten, so
    // their URLs already show the full path — don't strip.
    const primary = `/org/${PRIMARY_ORG}`
    return routePrefix.startsWith(primary) ? primary : ''
  }
  // Custom host: the proxy always rewrote based on hostname resolution, so
  // the first tenant segment is exactly what was prepended.
  const m = routePrefix.match(/^(\/org\/[^/]+|\/[^/]+)/)
  return m ? m[1] : ''
}

export function stripPrefix(path: string, prefix?: string): string {
  if (!prefix) return path
  if (path === prefix) return ''
  if (path.startsWith(`${prefix}/`)) return path.slice(prefix.length)
  return path
}

export function PublicSiteLayout({
  teacher,
  siteStructure,
  rootSkripts = [],
  sidebarItems,
  children,
  currentPath: currentPathProp,
  fullSiteStructure,
  sidebarBehavior: sidebarBehaviorProp = 'full',
  theme: themeProp,
  pageId: pageIdProp,
  routePrefix,
  homeUrl,
  hideSidebar: hideSidebarProp
}: PublicSiteLayoutProps) {
  const router = useRouter()

  // Inside the dashboard theme editor's preview iframe, unsaved settings
  // replace the server-rendered ones (src/components/public/theme-preview.ts).
  const previewOverride = useThemePreviewOverride()
  const sidebarBehavior = previewOverride?.sidebarBehavior ?? sidebarBehaviorProp
  const themePreset = previewOverride?.preset ?? themeProp?.preset ?? 'modern'
  const bodyFont = previewOverride ? previewOverride.bodyFont : (themeProp?.bodyFont ?? null)
  const showReadingProgress = previewOverride?.showReadingProgress ?? themeProp?.showReadingProgress ?? true
  const customCss = previewOverride ? previewOverride.customCss : (themeProp?.customCss ?? '')

  // Route-derived state — preferred over props when the layout sits above the
  // page component (App Router shared layout), because the params change with
  // each navigation without unmounting the sidebar. Props still win when
  // explicitly passed (used by org/preview routes that haven't been lifted).
  const routeParams = useParams<{
    skriptSlug?: string
    pageSlug?: string
    contentPageSlug?: string
  }>()
  const paramSkriptSlug = routeParams?.skriptSlug
  const paramPageSlug = routeParams?.pageSlug ?? routeParams?.contentPageSlug

  const derivedCurrentPath = paramSkriptSlug
    ? `/${paramSkriptSlug}${paramPageSlug ? `/${paramPageSlug}` : ''}`
    : undefined

  const currentPath = currentPathProp ?? derivedCurrentPath

  // Locate current page in siteStructure to derive pageId + hideSidebar.
  // Only used when caller didn't pass them explicitly.
  const currentPage = useMemo(() => {
    if (!paramSkriptSlug || !paramPageSlug) return undefined
    for (const col of siteStructure) {
      for (const skript of col.skripts) {
        if (skript.slug !== paramSkriptSlug) continue
        const found = skript.pages.find(p => p.slug === paramPageSlug)
        if (found) return found
      }
    }
    // Root-level skripts (placed directly, not inside a collection) were
    // missing here — currentPage/pageId silently stayed undefined for them,
    // which meant AuthButton never fetched can-edit and never showed the
    // "Edit this page" affordance for their author.
    for (const skript of rootSkripts) {
      if (skript.slug !== paramSkriptSlug) continue
      const found = skript.pages.find(p => p.slug === paramPageSlug)
      if (found) return found
    }
    return undefined
  }, [siteStructure, rootSkripts, paramSkriptSlug, paramPageSlug])

  const { pageId: reportedPageId } = useCurrentSite()
  const pageId = pageIdProp ?? currentPage?.id ?? reportedPageId ?? undefined
  const hideSidebar = hideSidebarProp ?? currentPage?.pageType === 'exam'

  // Unified, site-builder-ordered sidebar list. When the server provides
  // `sidebarItems` (interleaved), use it; otherwise reconstruct collections-
  // first then root skripts to keep callers that haven't migrated working.
  // Contextual mode filters to the single item containing the current skript.
  const displaySidebarItems = useMemo<SidebarItem[]>(() => {
    // Routes that render per-page (org routes) pass a single-skript
    // siteStructure plus the tenant-wide fullSiteStructure. In full mode the
    // latter must win, or the sidebar collapses to the current skript and
    // looks contextual regardless of the setting (regression from 3409746a,
    // which dropped the old displayStructure fallback).
    const structure = sidebarBehavior === 'full' && fullSiteStructure
      ? fullSiteStructure
      : siteStructure
    const base: SidebarItem[] = sidebarItems ?? [
      ...structure.map(c => ({ kind: 'collection' as const, data: c })),
      ...rootSkripts.map(s => ({ kind: 'skript' as const, data: s })),
    ]
    if (sidebarBehavior !== 'contextual' || !paramSkriptSlug) return base
    for (const item of base) {
      if (item.kind === 'collection') {
        const skript = item.data.skripts.find(s => s.slug === paramSkriptSlug)
        if (skript) {
          return [{ kind: 'collection', data: { ...item.data, skripts: [skript] } }]
        }
      } else if (item.data.slug === paramSkriptSlug) {
        return [item]
      }
    }
    return []
  }, [sidebarItems, siteStructure, fullSiteStructure, rootSkripts, sidebarBehavior, paramSkriptSlug])

  // Base prefix for nav URLs. routePrefix is the full internal server path;
  // we strip whatever the proxy prepended for the current hostname so links
  // match what the browser actually shows. SSR returns the full prefix
  // (window unavailable) — the first client render corrects it.
  const getBasePrefix = () => {
    const full = routePrefix ?? `/${teacher.pageSlug}`
    if (typeof window === 'undefined') return full
    return stripPrefix(full, computeProxyStrip(window.location.hostname, full))
  }

  // Home/logo target. Falls back to '/' when stripping leaves an empty path
  // (the tenant root on its primary domain).
  const getHomeUrl = () => {
    const raw = homeUrl ?? routePrefix ?? `/${teacher.pageSlug}`
    if (typeof window === 'undefined') return raw || '/'
    return stripPrefix(raw, computeProxyStrip(window.location.hostname, raw)) || '/'
  }
  const { data: session } = useSession()
  const { setSidebarCollapsed: setSidebarCollapsedInContext, sidebarWidth } = useLayout()
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)

  // Track last visited teacher page for students (used for "Back to" link, signout redirect, and nav logo)
  // Runs on every page navigation (currentPath dep) so href always reflects the exact current lesson
  useEffect(() => {
    if (session?.user?.accountType === 'student' && teacher.pageSlug) {
      const lastTeacherPage = {
        slug: teacher.pageSlug,
        name: teacher.pageName || teacher.name || teacher.pageSlug,
        pageIcon: teacher.pageIcon ?? null,
        href: window.location.href,
      }
      localStorage.setItem('lastTeacherPage', JSON.stringify(lastTeacherPage))
    }
  }, [session?.user?.accountType, teacher.pageSlug, teacher.pageName, teacher.name, teacher.pageIcon, currentPath])

  // Sync local sidebar collapse state with global context
  useEffect(() => {
    setSidebarCollapsedInContext(isSidebarCollapsed)
  }, [isSidebarCollapsed, setSidebarCollapsedInContext])
  
  // Storage keys for persistence
  const EXPANDED_SCRIPTS_KEY = `expanded-collections-${teacher.pageSlug}`
  const EXPANDED_SKRIPTS_KEY = `expanded-skripts-${teacher.pageSlug}`
  const SIDEBAR_SCROLL_KEY = `sidebar-scroll-${teacher.pageSlug}`

  // Ref for sidebar navigation scroll container
  const sidebarNavRef = useRef<HTMLDivElement>(null)

  // Initialize with all collections expanded for SSR, then hydrate from localStorage
  const [expandedCollections, setExpandedCollections] = useState<string[]>(
    () => siteStructure.map(collection => collection.id)
  )

  // Initialize expandedSkripts as empty for SSR, then hydrate from localStorage
  const [expandedSkripts, setExpandedSkripts] = useState<string[]>([])

  // Track if we've finished first render (for enabling animations after mount)
  const [isInitialized, setIsInitialized] = useState(false)

  // Hydrate expanded state from localStorage after mount to avoid hydration mismatch
  useEffect(() => {
    // Hydrate expandedCollections
    const storedCollections = localStorage.getItem(EXPANDED_SCRIPTS_KEY)
    if (storedCollections) {
      try {
        const parsed = JSON.parse(storedCollections)
        if (Array.isArray(parsed)) {
          setExpandedCollections(parsed)
        }
      } catch {
        // Keep default (all expanded)
      }
    }

    // Hydrate expandedSkripts
    const storedSkripts = localStorage.getItem(EXPANDED_SKRIPTS_KEY)
    let expandedFromStorage: string[] = []

    if (storedSkripts) {
      try {
        expandedFromStorage = JSON.parse(storedSkripts)
      } catch {
        expandedFromStorage = []
      }
    }

    // Also auto-expand skripts that contain the current page
    if (currentPath) {
      siteStructure.forEach(collection => {
        collection.skripts.forEach(skript => {
          const hasCurrentPage = skript.pages.some(page =>
            currentPath === `/${skript.slug}/${page.slug}`
          )
          if (hasCurrentPage && !expandedFromStorage.includes(skript.id)) {
            expandedFromStorage.push(skript.id)
          }
        })
      })
      // Also check root skripts
      rootSkripts.forEach(skript => {
        const hasCurrentPage = skript.pages.some(page =>
          currentPath === `/${skript.slug}/${page.slug}`
        )
        if (hasCurrentPage && !expandedFromStorage.includes(skript.id)) {
          expandedFromStorage.push(skript.id)
        }
      })
    }

    setExpandedSkripts(expandedFromStorage)

    // Delay enabling animations until after the state has been applied
    // This prevents the collapse→expand flash on initial load
    requestAnimationFrame(() => {
      setIsInitialized(true)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on mount
  }, [])

  // Update expanded skripts when current path changes
  useEffect(() => {
    if (!isInitialized || !currentPath) return
    
    const newExpandedSkripts = [...expandedSkripts]
    let hasChanges = false
    
    siteStructure.forEach(collection => {
      collection.skripts.forEach(skript => {
        const hasCurrentPage = skript.pages.some(page =>
          currentPath === `/${skript.slug}/${page.slug}`
        )
        if (hasCurrentPage && !newExpandedSkripts.includes(skript.id)) {
          newExpandedSkripts.push(skript.id)
          hasChanges = true
        }
      })
    })

    if (hasChanges) {
       
      setExpandedSkripts(newExpandedSkripts)
    }
  }, [currentPath, isInitialized, expandedSkripts, siteStructure])

  // Persist expanded collections to localStorage
  useEffect(() => {
    if (!isInitialized) return
    localStorage.setItem(EXPANDED_SCRIPTS_KEY, JSON.stringify(expandedCollections))
  }, [expandedCollections, isInitialized, EXPANDED_SCRIPTS_KEY])

  // Persist expanded skripts to localStorage
  useEffect(() => {
    if (!isInitialized) return
    localStorage.setItem(EXPANDED_SKRIPTS_KEY, JSON.stringify(expandedSkripts))
  }, [expandedSkripts, isInitialized, EXPANDED_SKRIPTS_KEY])

  // Restore sidebar scroll position after mount
  useEffect(() => {
    if (!isInitialized || !sidebarNavRef.current) return
    const savedScroll = sessionStorage.getItem(SIDEBAR_SCROLL_KEY)
    if (savedScroll) {
      sidebarNavRef.current.scrollTop = parseInt(savedScroll, 10)
    }
  }, [isInitialized, SIDEBAR_SCROLL_KEY])

  // Save sidebar scroll position on scroll
  useEffect(() => {
    const nav = sidebarNavRef.current
    if (!nav) return

    const handleScroll = () => {
      sessionStorage.setItem(SIDEBAR_SCROLL_KEY, nav.scrollTop.toString())
    }

    nav.addEventListener('scroll', handleScroll, { passive: true })
    return () => nav.removeEventListener('scroll', handleScroll)
  }, [SIDEBAR_SCROLL_KEY])

  // Apply paper scale for narrow viewports (< 1280px)
  // This scales the paper to fit viewport width while maintaining fixed internal dimensions
  useEffect(() => {
    const PAPER_WIDTH = 1280

    const updatePaperScale = () => {
      const vw = window.innerWidth
      if (vw < PAPER_WIDTH) {
        const scale = vw / PAPER_WIDTH
        document.documentElement.style.setProperty('--paper-scale', scale.toString())
        // Negative margin to compensate for scaled height
        const marginAdjust = `calc(-1 * (1 - ${scale}) * var(--paper-height, 0px))`
        document.documentElement.style.setProperty('--paper-scale-margin', marginAdjust)
      } else {
        document.documentElement.style.removeProperty('--paper-scale')
        document.documentElement.style.removeProperty('--paper-scale-margin')
      }
    }

    updatePaperScale()
    window.addEventListener('resize', updatePaperScale)
    return () => window.removeEventListener('resize', updatePaperScale)
  }, [])

  const toggleSkript = (skriptId: string) => {
    setExpandedSkripts(prev => 
      prev.includes(skriptId) 
        ? prev.filter(id => id !== skriptId)
        : [...prev, skriptId]
    )
  }

  const isCurrentPage = (skriptSlug: string, pageSlug: string) => {
    return currentPath === `/${skriptSlug}/${pageSlug}`
  }

  const navigateToPage = (skriptSlug: string, pageSlug: string) => {
    // Use getBasePrefix() at click time for correct custom domain handling
    const url = `${getBasePrefix()}/${skriptSlug}/${pageSlug}`

    router.push(url)
    setIsSidebarOpen(false)
  }

  const navigateToSkript = (skriptSlug: string, skriptId: string, hasFrontpage?: boolean) => {
    if (hasFrontpage) {
      // Navigate to skript frontpage and expand the skript
      const url = `${getBasePrefix()}/${skriptSlug}`

      // Ensure the skript is expanded
      if (!expandedSkripts.includes(skriptId)) {
        setExpandedSkripts(prev => [...prev, skriptId])
      }

      router.push(url)
      setIsSidebarOpen(false)
    } else {
      // No frontpage - just toggle expand/collapse
      toggleSkript(skriptId)
    }
  }

  return (
    <TeacherClassProvider>
      {/* Reloads a logged-in student's tab when their class's lockdown toggles,
          so the middleware gate re-applies live. No-op for anon/teacher tabs. */}
      <LockdownWatcher />
      <div
        className="min-h-screen bg-background overflow-visible"
        data-typography={themePreset}
        data-body-font={bodyFont ?? undefined}
        style={{ '--sidebar-width': `${sidebarWidth}px` } as React.CSSProperties}
      >
        {/* Site custom CSS. Sanitized server-side (readSiteTheme) — no
            external url()/@import, no "<" — so it is safe as raw style text. */}
        {customCss && <style data-site-custom-css="" dangerouslySetInnerHTML={{ __html: customCss }} />}
        {showReadingProgress && <ReadingProgress />}

      <PrintView pageId={pageId} />
      {/* Top-right page controls, on every screen size (the sidebar has no copy).
          Hidden in the print preview, whose bar has the controls that matter there. */}
      <div className="floating-page-controls print-hide print:hidden fixed top-8 right-4 z-50 flex items-center gap-2">
        <AdminToolbox pageId={pageId} />
        <FontSizeControls />
        <ReflowToggle />
        <PrintButton />
        <PublicSoundToggle />
        <PublicThemeToggle />
        <AuthButton pageId={pageId} teacherPageSlug={teacher.pageSlug} teacherBillingPlan={teacher.billingPlan} isOrgPage={routePrefix?.startsWith('/org/')} orgSlug={routePrefix?.startsWith('/org/') ? routePrefix.split('/')[2] : undefined} />
      </div>

      {/* Mobile menu button */}
      {!hideSidebar && (
      <div className="print-hide min-[1344px]:hidden print:hidden fixed top-8 left-4 z-50">
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="p-2 bg-card rounded-md shadow-md border border-border"
        >
          {isSidebarOpen ? <X className="w-5 h-5 text-foreground" /> : <Menu className="w-5 h-5 text-foreground" />}
        </button>
      </div>
      )}

      {/* Sidebar */}
      {!hideSidebar && (
      <div className={`print-hide fixed inset-y-0 left-0 z-40 print:hidden bg-card paper-shadow transform transition-all duration-300 ease-in-out ${
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } min-[1344px]:translate-x-0 ${
        isSidebarCollapsed ? 'w-16 min-w-16' : 'w-80'
      }`}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className={`border-b border-border ${isSidebarCollapsed ? 'p-4' : 'p-6'}`}>
            {isSidebarCollapsed ? (
              /* Collapsed sidebar header */
              <div className="flex flex-col items-center gap-2">
                {/* Icon: custom URL, default NotebookPen, or letter placeholder - clickable as home link */}
                <button
                  onClick={() => {
                    router.push(getHomeUrl())
                    setIsSidebarOpen(false)
                  }}
                  className="cursor-pointer"
                  title="Go to homepage"
                >
                  {teacher.pageIcon === 'default' ? (
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      <NotebookPen className="w-6 h-6 text-muted-foreground" />
                    </div>
                  ) : teacher.pageIcon ? (
                    <div className="relative w-10 h-10 rounded-lg overflow-hidden shrink-0 bg-background">
                      <Image
                        src={teacher.pageIcon}
                        alt="Page icon"
                        fill
                        className="object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      <span className="text-muted-foreground text-lg font-heading">
                        {(teacher.pageName || teacher.name || 'P').charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                  className="p-2"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            ) : (
              /* Expanded sidebar header */
              <>
                {/* Row 1: wide logo, or Icon + Page name (home link), collapse button on the right.
                    No balancing spacer on the left: it would cost 36px and truncate names like
                    "informatikgarten.ch" (icon + name = 226px of 272px). The title is therefore
                    centred in the space left of the button, ~20px left of the sidebar centre. */}
                <div className="flex items-center gap-1">
                <button
                  onClick={() => {
                    router.push(getHomeUrl())
                    setIsSidebarOpen(false)
                  }}
                  className="flex min-w-0 flex-1 items-center justify-center gap-3 cursor-pointer"
                  title="Go to homepage"
                >
                  {teacher.titleStyle === 'logo' && teacher.logoUrl ? (
                    <div className="relative h-10 w-full">
                      <Image
                        src={teacher.logoUrl}
                        alt={teacher.pageName || teacher.name || 'Logo'}
                        fill
                        className="object-contain object-left"
                      />
                    </div>
                  ) : (
                    <>
                      {/* Icon: custom URL, default NotebookPen, or letter placeholder */}
                      {teacher.pageIcon === 'default' ? (
                        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                          <NotebookPen className="w-6 h-6 text-muted-foreground" />
                        </div>
                      ) : teacher.pageIcon ? (
                        <div className="relative w-10 h-10 rounded-lg overflow-hidden shrink-0 bg-background">
                          <Image
                            src={teacher.pageIcon}
                            alt="Page icon"
                            fill
                            className="object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                          <span className="text-muted-foreground text-lg font-heading">
                            {(teacher.pageName || teacher.name || 'P').charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="text-2xl font-bold text-foreground truncate font-heading">
                        {teacher.pageName || teacher.name || 'Untitled Page'}
                      </div>
                    </>
                  )}
                </button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                  className="w-9 shrink-0 p-2"
                  title="Collapse sidebar"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                </div>

                {/* Row 2: Description (if exists) - supports markdown links */}
                {teacher.pageDescription && (
                  <p className="text-sm text-muted-foreground mt-2 text-center">
                    <InlineMarkdown>{teacher.pageDescription}</InlineMarkdown>
                  </p>
                )}

              </>
            )}
          </div>

          {/* Navigation. p-4 (16px right padding) gives the scrollbar
              breathing room from the content without insetting the
              container itself. The footer (class toolbar + sync + legal)
              is a SIBLING of this div, not a child, so nav content can
              scroll beneath a fixed footer when it overflows. */}
          <div
            ref={sidebarNavRef}
            className="flex-1 min-h-0 p-4 overflow-y-scroll [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&:hover::-webkit-scrollbar-thumb]:bg-muted-foreground/30"
          >
            {isSidebarCollapsed ? (
              /* Collapsed navigation - empty, use header icon to go home */
              <nav className="space-y-2" />
            ) : (
              /* Full navigation when expanded */
              <nav className="space-y-2">
              {/* displaySidebarItems interleaves collections and root skripts
                  in site-builder order. No "Individual Skripts" grouping —
                  root skripts render as standalone items between collections,
                  exactly where the teacher placed them. */}
              {(() => {
                // Decorative chevron when contextual mode has filtered to a
                // single skript (whether inside a collection or at root).
                const isContextualSingle = sidebarBehavior === 'contextual' && (
                  displaySidebarItems.length === 1 &&
                  (displaySidebarItems[0].kind === 'skript' ||
                    (displaySidebarItems[0].kind === 'collection' &&
                      displaySidebarItems[0].data.skripts.length === 1))
                )
                // Root skripts render with no letter marker; collection
                // skripts keep their per-collection letter (handled below).
                return (
                  <>
                    {displaySidebarItems.map((item, index) => {
                      if (item.kind === 'collection') {
                        const collection = item.data
                        return (
                <div key={`col-${collection.id}`} className={`${index > 0 ? 'mt-5' : ''} mb-4`}>
                  {/* Collection Title - Docusaurus-style category header */}
                  <div className="py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {collection.title}
                  </div>

                  {/* Skripts - always visible */}
                  <div className="space-y-1.5 mt-1">
                      {collection.skripts.map((skript, skriptIndex) => (
                        <div key={skript.id}>
                          {/* Skript Title - letter marker, title, chevron */}
                          <div
                            className={`flex items-center w-full text-left py-1 text-sm rounded-md transition-colors ${
                              expandedSkripts.includes(skript.id)
                                ? 'text-foreground font-medium bg-muted/50'
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                            }`}
                          >
                            {/* Letter marker - uses skript.order (position in collection) or fallback to index */}
                            <span
                              className="w-5 h-5 rounded text-xs font-bold flex items-center justify-center mr-2 shrink-0 text-white"
                              style={{ backgroundColor: collection.accentColor || '#6b7280' }}
                            >
                              {String.fromCharCode(65 + (skript.order ?? skriptIndex))}
                            </span>
                            {/* Title - navigate to frontpage (if exists) or toggle expand */}
                            <button
                              onClick={() => navigateToSkript(skript.slug, skript.id, skript.hasFrontpage)}
                              className="truncate flex-1 text-left hover:text-primary"
                            >
                              {skript.title}
                            </button>
                            {/* Chevron - toggle only, right-aligned (non-interactive in contextual single skript mode) */}
                            {isContextualSingle ? (
                              <span className="p-1.5 ml-1 shrink-0 text-muted-foreground">
                                <ChevronDown className="w-4 h-4" />
                              </span>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  toggleSkript(skript.id)
                                }}
                                className="p-1.5 ml-1 hover:bg-muted rounded shrink-0 text-muted-foreground cursor-pointer"
                                aria-label={expandedSkripts.includes(skript.id) ? 'Collapse' : 'Expand'}
                              >
                                {expandedSkripts.includes(skript.id) ? (
                                  <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <ChevronRight className="w-4 h-4" />
                                )}
                              </button>
                            )}
                          </div>

                          {/* Pages - with smooth slide transition (disabled until initialized to prevent flash) */}
                          <div className={`grid ${isInitialized ? 'transition-[grid-template-rows] duration-200 ease-out' : ''} ${
                            expandedSkripts.includes(skript.id) ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                          }`}>
                            <div className="overflow-hidden">
                              <div className="space-y-0.5 py-1 ml-4">
                                {skript.pages.map((page, pageIndex) => (
                                  <button
                                    key={page.id}
                                    onClick={() => navigateToPage(skript.slug, page.slug)}
                                    className={`flex items-center w-full text-left py-1.5 px-2 text-sm rounded-md transition-colors ${
                                      isCurrentPage(skript.slug, page.slug)
                                        ? 'text-primary font-medium bg-primary/10'
                                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                                    }`}
                                  >
                                    {/* Page number marker */}
                                    <span className="w-5 h-5 rounded text-xs font-medium flex items-center justify-center mr-2 shrink-0 bg-muted text-muted-foreground">
                                      {pageIndex + 1}
                                    </span>
                                    <span className="truncate">{page.title}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                </div>
                        )
                      }
                      // Root skript — render inline, no surrounding section
                      // and no letter marker (those are reserved for skripts
                      // inside collections, where the letter indicates order).
                      const skript = item.data
                      return (
                <div key={`root-${skript.id}`} className={`${index > 0 ? 'mt-5' : ''} mb-4`}>
                  <div
                    className={`flex items-center w-full text-left text-sm rounded-md transition-colors ${
                      expandedSkripts.includes(skript.id)
                        ? 'text-foreground font-medium bg-muted/50'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    <button
                      onClick={() => navigateToSkript(skript.slug, skript.id, skript.hasFrontpage)}
                      className="truncate flex-1 text-left hover:text-primary"
                    >
                      {skript.title}
                    </button>
                    {isContextualSingle ? (
                      <span className="p-1.5 ml-1 shrink-0 text-muted-foreground">
                        <ChevronDown className="w-4 h-4" />
                      </span>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleSkript(skript.id)
                        }}
                        className="p-1.5 ml-1 hover:bg-muted rounded shrink-0 text-muted-foreground cursor-pointer"
                        aria-label={expandedSkripts.includes(skript.id) ? 'Collapse' : 'Expand'}
                      >
                        {expandedSkripts.includes(skript.id) ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>
                  <div className={`grid ${isInitialized ? 'transition-[grid-template-rows] duration-200 ease-out' : ''} ${
                    expandedSkripts.includes(skript.id) ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                  }`}>
                    <div className="overflow-hidden">
                      <div className="space-y-0.5 py-1 ml-4">
                        {skript.pages.map((page, pageIndex) => (
                          <button
                            key={page.id}
                            onClick={() => navigateToPage(skript.slug, page.slug)}
                            className={`flex items-center w-full text-left py-1.5 px-2 text-sm rounded-md transition-colors ${
                              isCurrentPage(skript.slug, page.slug)
                                ? 'text-primary font-medium bg-primary/10'
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                            }`}
                          >
                            <span className="w-5 h-5 rounded text-xs font-medium flex items-center justify-center mr-2 shrink-0 bg-muted text-muted-foreground">
                              {pageIndex + 1}
                            </span>
                            <span className="truncate">{page.title}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                      )
                    })}
                  </>
                )
              })()}
              </nav>
            )}
          </div>

          {/* Footer: class toolbar (top row) + sync + legal/terms/built-with
              (bottom row). Sibling of the scrollable nav (NOT a child) so the
              footer stays pinned to the sidebar's bottom edge while overflowed
              nav content scrolls beneath it. Top-edge gradient fades the
              scrolling content into the footer's bg-card. */}
          <div className={`relative shrink-0 ${isSidebarCollapsed ? 'p-2 pt-0 flex flex-col items-center gap-2' : 'p-4 pt-0 space-y-2'}`}>
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 -top-6 h-6 bg-linear-to-b from-transparent to-card"
            />
            {/* Slot is rendered in both collapsed and expanded modes —
                ClassToolbar reads `sidebarCollapsed` from useLayout and
                renders only the broadcast toggle when collapsed. */}
            <div id="class-toolbar-slot" />
            {/* Supporter badge — rendered into the ISR-cached HTML from the
                same cached teacher data as titleStyle; nothing per-visitor. */}
            {!isSidebarCollapsed &&
              teacher.billingPlan?.startsWith('supporter') &&
              !teacher.supporterBadgeHidden && (
                <div className="pb-2 flex justify-center">
                  <SupporterBadge
                    message={teacher.supporterBadgeMessage}
                    href="https://eduskript.org/supporters"
                  />
                </div>
              )}
            {/* Pioneer badge (src/lib/pioneer.ts), same cached teacher data. */}
            {!isSidebarCollapsed && teacher.billingPlan === PIONEER_PLAN_SLUG && (
              <div className="pb-2 flex justify-center">
                <PioneerBadge href="https://eduskript.org" />
              </div>
            )}
            <div className={isSidebarCollapsed ? '' : 'flex items-start justify-between gap-2'}>
              <SyncStatusButton />
              {!isSidebarCollapsed && (
                <div className="text-right text-[11px] text-muted-foreground/40 space-y-0.5 leading-tight">
                  {/* prefetch={false}: footer links are rarely clicked, but
                      Next 16 emits 2-3 RSC prefetches per visible Link
                      during idle time, which kept the public-page tab in
                      "loading" state for 4-5 s on Slow 4G even after LCP. */}
                  <div>
                    <Link href="/impressum" className="hover:text-muted-foreground" prefetch={false}>Legal</Link>
                    <span className="mx-1.5">·</span>
                    <Link href="/datenschutz" className="hover:text-muted-foreground" prefetch={false}>Privacy</Link>
                    <span className="mx-1.5">·</span>
                    <Link href="/terms" className="hover:text-muted-foreground" prefetch={false}>Terms ({TERMS_DATE})</Link>
                  </div>
                  <div>
                    Built with{' '}
                    <a
                      href="https://eduskript.org"
                      target="_blank"
                      rel="noopener"
                      className="hover:text-muted-foreground"
                    >
                      Eduskript
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Overlay for mobile */}
      {!hideSidebar && isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 min-[1344px]:hidden print:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

        {/* Main content with scroll container.
            relative: hosts the absolutely-positioned zoom-spacer that AnnotationLayer
            injects when the page is zoomed via transform: scale(). Declarative so
            AnnotationLayer doesn't have to mutate this element imperatively. */}
        <div
          id="scroll-container"
          className={`relative transition-all duration-300 h-screen overflow-auto ${
            hideSidebar ? 'no-sidebar' : isSidebarCollapsed ? 'min-[1344px]:ml-16' : 'min-[1344px]:ml-80'
          }`}
        >
          <main className="bg-background min-h-screen">
            {children}
          </main>
        </div>
      </div>
    </TeacherClassProvider>
  )
}
